import { useEffect, useRef, useState } from 'react'
import { Spatial, type SpatialFrame } from './Spatial'
import { useGraphFrame } from './useGraphFrame'
import { emitAfterDraw, requestRepaint } from './frameBus'
import { runCanvasLoop } from './cadence'
import { tapReach } from './tapReach'
import { Pointers } from './gesture'
import { captionDoubleTap, noteCaptionTap, setTouchTarget, takeLabelTap } from './labelTouch'
import { useStore } from '../core/store'
import { motion } from '../core/motion'
import { LOOK_MARGIN, visibleField } from '../core/framing'

/**
 * Host and control surface for the spatial view.
 *
 * A sibling of `NeuralCanvas` rather than a mode inside it, and the split is worth
 * defending: that file's loop is an easing controller for an affine viewport with a
 * follow-the-graph behaviour, and this one is an orbit rig. Merging them would put
 * a mode branch inside every one of those steps, and the two cameras have no state
 * in common beyond the graph they point at.
 *
 * ── The gesture model ─────────────────────────────────────────────────────
 *
 * Drag orbits. That is the decision everything else follows from, and it is the
 * opposite of the flat view, where drag pans. It is right here because an orbit is
 * the only gesture this view exists to offer — "see it from somewhere else" — and
 * because a 3D pan as the primary drag is how every clumsy 3D viewer feels: you
 * slide sideways for a while and never learn the shape of the thing.
 *
 * ⇧-drag pans, ⌥-drag raises the eye along the evidence axis, wheel dollies. The
 * modifiers match the flat view's wheel modifiers rather than inventing a second
 * vocabulary.
 */
export function SpatialCanvas() {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const rendererRef = useRef<Spatial | null>(null)
  const frameRef = useRef<SpatialFrame | null>(null)
  const [dragging, setDragging] = useState(false)
  // The same fact as `dragging`, kept where the gesture can read it. The pointer
  // effect below holds all of its state in a closure it builds once; reading the
  // React value there would make the effect depend on it, and the effect would
  // then tear down and rebuild mid-gesture — the moment the drag declares itself
  // — taking `down` and the rest of the gesture with it. The state is still what
  // renders; this is what the handlers decide on.
  const draggingRef = useRef(false)
  const [overNode, setOverNode] = useState(false)

  const { session, rev, visible, folded, focus, guideKeep, guideTargetId, palette, filters } =
    useGraphFrame()

  useEffect(() => {
    const s = useStore.getState()
    frameRef.current = {
      session,
      visible,
      folded,
      focus,
      guideKeep,
      guideTargetId,
      selectedId: s.selectedId,
      hoverId: s.hoverId,
      activeTargets: s.activeTargets,
      filters,
      palette,
      rev,
      tempo: s.tempo,
      playhead: s.playhead,
      playheadAt: s.playheadAt,
      rings: s.showRings,
    }
  }, [session, visible, folded, focus, guideKeep, guideTargetId, palette, filters, rev])

  // A new palette is a new picture, and it is owed even under the sheet it was
  // chosen in: Settings holds the loop, and its glass shows the field through it.
  // See `cadence.ts`.
  useEffect(() => requestRepaint(), [palette])

  useEffect(() => {
    const canvas = canvasRef.current
    const host = hostRef.current
    if (!canvas || !host) return

    const renderer = new Spatial(canvas)
    rendererRef.current = renderer

    const ro = new ResizeObserver(() => {
      const r = host.getBoundingClientRect()
      if (r.width > 0 && r.height > 0) renderer.resize(r.width, r.height)
      // A resized canvas is a cleared one, even on a loop that is holding.
      requestRepaint()
    })
    ro.observe(host)

    // One frame's worth of framing, once a real size exists. There is no
    // follow-the-graph easing here on purpose: a camera that re-framed itself
    // every time a node arrived would swing the whole scene around the viewer
    // while they were trying to look at something, which in an orbit rig is
    // actively unpleasant rather than merely distracting. The graph grows and you
    // stay where you are; Fit is one key away.
    let framed = false

    let lastT = performance.now()
    // One frame, answering whether anything is still live — the flat loop's
    // contract with `cadence.ts`, with the orbit rig's target in place of the
    // viewport's.
    const frame = (t: number): boolean => {
      const dt = Math.min(120, Math.max(1, t - lastT))
      lastT = t

      const f = frameRef.current
      if (!f) {
        emitAfterDraw()
        return true
      }
      const s = useStore.getState()
      const { w, h } = renderer.size
      // The part of the stage the panes and the composer leave, the flat
      // camera's own field (D159). The picture is centred in it (`Spatial.aim`).
      const field = w > 0 && h > 0 ? visibleField(w, h, s.viewInset) : null
      if (field) renderer.aim(field)
      if (!framed && field) {
        framed = true
        s.frameCam(w, h)
      }
      // A node picked from a list: flown to only if it is out of the field. The
      // same request the flat loop answers — one door, whichever view is up.
      // The store's own 3D pass judges against the whole stage, which the
      // centred picture only ever passes more generously (see `Spatial.aim`), so
      // the field is judged here first; once the pivot is under way the store's
      // pass finds the node at the centre and only clears the request.
      if (s.viewAsk && field) {
        const n = s.session.nodes[s.viewAsk]
        if (n && !renderer.seen(s.camTarget ?? s.cam, n, field, LOOK_MARGIN)) {
          s.pivotOn(n.id, false)
        }
        useStore.getState().settleLook(w, h)
      }

      // Ease toward the vantage point, if one is set. Same exponential smoothing
      // and the same far-gap snap as the flat view: easing is for continuity, and
      // there is none to preserve when the target is nowhere near where you are.
      //
      // Yaw takes the SHORT way round. Interpolating the raw numbers would send the
      // camera the long way whenever a flight crossed the seam, which reads as the
      // scene spinning for no reason — and after enough orbiting, `yaw` is a large
      // number rather than one inside a single turn.
      const tgt = s.camTarget
      if (tgt) {
        const c = s.cam
        let dYaw = (tgt.yaw - c.yaw) % (Math.PI * 2)
        if (dYaw > Math.PI) dYaw -= Math.PI * 2
        if (dYaw < -Math.PI) dYaw += Math.PI * 2
        const dTarget = Math.hypot(
          tgt.target.x - c.target.x,
          tgt.target.y - c.target.y,
          tgt.target.z - c.target.z,
        )
        const far =
          Math.abs(Math.log(tgt.dist / Math.max(1, c.dist))) > 0.85 ||
          dTarget > Math.max(900, c.dist * 0.9) ||
          Math.abs(dYaw) > 1.5
        if (far) {
          s.applyCam(tgt, true)
        } else {
          const a = 1 - Math.exp(-dt / 300)
          const next = {
            yaw: c.yaw + dYaw * a,
            pitch: c.pitch + (tgt.pitch - c.pitch) * a,
            dist: c.dist + (tgt.dist - c.dist) * a,
            target: {
              x: c.target.x + (tgt.target.x - c.target.x) * a,
              y: c.target.y + (tgt.target.y - c.target.y) * a,
              z: c.target.z + (tgt.target.z - c.target.z) * a,
            },
          }
          const arrived =
            Math.abs(dYaw) < 0.002 &&
            Math.abs(tgt.pitch - next.pitch) < 0.002 &&
            Math.abs(tgt.dist - next.dist) < tgt.dist * 0.002 &&
            dTarget < 1.5
          s.applyCam(arrived ? tgt : next, arrived)
        }
      }

      f.selectedId = s.selectedId
      f.hoverId = s.hoverId
      f.activeTargets = s.activeTargets
      f.tempo = s.tempo
      f.playhead = s.playhead
      f.playheadAt = s.playheadAt
      f.rings = s.showRings

      renderer.draw(f, s.cam, dt, s.busy)
      emitAfterDraw()
      const after = useStore.getState()
      return (
        after.busy ||
        after.playing ||
        after.run?.phase === 'moving' ||
        after.camTarget !== null ||
        after.viewAsk !== null ||
        motion.moving ||
        renderer.settling
      )
    }
    const stop = runCanvasLoop(host, frame)

    return () => {
      stop()
      ro.disconnect()
      rendererRef.current = null
    }
  }, [])

  /* ── pointer ───────────────────────────────────────────────────────── */

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    // Every variable below is the gesture in progress, and it lives in this
    // closure — so this effect must not re-subscribe while a gesture is
    // running. It used to depend on `dragging`, which is set by the gesture
    // itself the moment it passes the slop threshold: React re-rendered, the
    // effect tore itself down and rebuilt with `down` back to false, and every
    // event after the first was read as a hover. The orbit turned about 4px and
    // stopped, ⇧-pan and ⌥-raise with it, a pointerup was swallowed by
    // `if (!down) return`, and the canvas was left wearing the grabbing cursor
    // with nothing being grabbed. The flag is a ref now, because it is state
    // the *loop* owns and only the cursor reads. Same fix as NeuralCanvas.
    //
    // Each pointer is kept by its own id, as on the flat canvas (`Pointers`,
    // D340). Two fingers dolly by their span and pan by their midpoint — the
    // wheel's dolly and the ⇧-drag's pan, so a touch screen reaches both without
    // a modifier key it does not have.
    const pointers = new Pointers()
    let down = false
    let moved = 0

    const local = (e: PointerEvent | WheelEvent) => {
      const r = canvas.getBoundingClientRect()
      return { x: e.clientX - r.left, y: e.clientY - r.top }
    }

    const declareDrag = () => {
      if (draggingRef.current) return
      draggingRef.current = true
      setDragging(true)
    }

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return
      // A pointer held here with no capture lost its up on the way, as on the
      // flat canvas (D340); left in, it would turn this press into a pinch.
      for (const id of pointers.ids())
        if (!canvas.hasPointerCapture(id)) {
          pointers.up(id)
          takeLabelTap(id)
        }
      // A press the browser itself delivered here never began on a caption — a
      // caption's is replayed, untrusted — so any entry under its id is stale.
      if (e.isTrusted) takeLabelTap(e.pointerId)
      const landed = pointers.down(e.pointerId, e.clientX, e.clientY)
      canvas.setPointerCapture(e.pointerId)
      down = true
      if (landed === 'lead') {
        moved = 0
        // A new press forgets the last caption tap; its own end notes it again
        // if it is one, so only a real second tap makes a caption's double-tap.
        noteCaptionTap(null)
        if (draggingRef.current) {
          draggingRef.current = false
          setDragging(false)
        }
      } else if (landed === 'pinch') declareDrag()
    }

    const onMove = (e: PointerEvent) => {
      const f = frameRef.current
      const r = rendererRef.current
      if (!f || !r) return

      if (down) {
        const step = pointers.move(e.pointerId, e.clientX, e.clientY)
        if (!step) return
        if (step.kind === 'pinch') {
          const st = useStore.getState()
          // Fingers parting bring the eye closer: the dolly's factor is the
          // distance's, so it is the span's ratio turned over. Both writes are
          // the reader's own, and clear any flight under way, as the wheel does.
          if (step.factor !== 1) st.dollyCam(1 / step.factor)
          if (step.dx !== 0 || step.dy !== 0) st.panCam(step.dx, step.dy, r.size.w)
          return
        }
        const { dx, dy } = step
        // The finger a pinch left behind goes on as one finger does here, from
        // where it is and with no slop to cross.
        if (!pointers.pinched) moved += Math.abs(dx) + Math.abs(dy)
        if (pointers.pinched || moved > 4) {
          declareDrag()
          const st = useStore.getState()
          if (e.shiftKey) st.panCam(dx, dy, r.size.w)
          else if (e.altKey) st.raiseCam(-dy * (st.cam.dist / 900))
          // Drag up and the eye RISES over the graph; drag right and the scene
          // turns to the right.
          //
          // Worth defending, because there is a competing convention. Under the
          // grab-and-drag metaphor the flat view's pan uses, dragging up would tip
          // the near rim of the disc toward you and show you its underside — the
          // opposite of this. Two things settle it: the arrow keys below, where "up
          // means up" is the only sane reading and which would otherwise contradict
          // the mouse; and the fact that this is what almost every 3D view on the
          // web does, so it is what a hand arrives already expecting. A rotation is
          // a different gesture from a translation, and it does not owe the pan its
          // feel.
          else st.orbitCam(-dx * 0.006, -dy * 0.005)
        }
        return
      }

      const p = local(e)
      const hit = r.hitTest(f, p.x, p.y)
      useStore.getState().hover(hit)
      setOverNode(!!hit)
    }

    const onUp = (e: PointerEvent) => {
      if (!pointers.has(e.pointerId)) return
      // Taken on every end, so no caption press outlives its own.
      const caption = takeLabelTap(e.pointerId)
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId)
      // One finger of several lifting ends nothing: the rest carry the gesture.
      if (!pointers.up(e.pointerId)) return
      down = false
      draggingRef.current = false
      setDragging(false)
      // A pinch is never a tap, however little its last finger travelled, and
      // neither is a press the system took away (pointercancel, or a capture
      // lost with no up).
      if (moved > 4 || pointers.pinched || e.type !== 'pointerup') return
      // A tap that began on a caption is the caption's tap: its node, whatever
      // mark lies under the fingertip (D340).
      // Remembered either way, so the browser's double-click that may follow
      // knows whose it is (`captionDoubleTap`).
      noteCaptionTap(caption ?? null)
      if (caption) {
        const st = useStore.getState()
        // ⌥ folds, as it does on the caption itself — except the seed, which
        // `toggleCollapse` refuses, so ⌥ there falls through to a select.
        if (e.altKey && st.session.nodes[caption]?.parentId) {
          st.toggleCollapse(caption)
          return
        }
        st.select(caption)
        st.setRail(true)
        return
      }
      const f = frameRef.current
      const r = rendererRef.current
      if (!f || !r) return
      const p = local(e)
      // A finger's tap reaches further (`tapReach`, D335) — but only when the
      // ordinary reach found nothing. Here the pick among overlapping marks is
      // the one nearest the EYE, not the screen point, so a wide reach tried
      // first could hand a tap to a mark in front of the one it landed on.
      const reach = tapReach(e)
      const hit = r.hitTest(f, p.x, p.y) ?? (reach ? r.hitTest(f, p.x, p.y, reach) : null)
      if (e.altKey && hit) {
        useStore.getState().toggleCollapse(hit)
        return
      }
      useStore.getState().select(hit)
      if (hit) useStore.getState().setRail(true)
    }

    /**
     * Double-click flies to a node and makes it the pivot.
     *
     * The flat view's double-click isolates a branch, and that still happens — but
     * in a volume the more valuable half is that the thing you clicked becomes what
     * you orbit around. Orbiting a graph about a point you are not looking at is
     * the single most disorienting thing an orbit camera does.
     */
    const onDouble = (e: PointerEvent) => {
      // A double-tap on a caption is the caption's: it focuses that branch, as
      // a double-click on the words always has (D340).
      const caption = captionDoubleTap()
      if (caption) {
        const st = useStore.getState()
        st.setFocus(st.focusId === caption ? null : caption)
        return
      }
      const f = frameRef.current
      const r = rendererRef.current
      if (!f || !r) return
      const p = local(e)
      const hit = r.hitTest(f, p.x, p.y)
      const st = useStore.getState()
      if (hit) {
        st.select(hit)
        st.pivotOn(hit)
      } else {
        st.setFocus(null)
        const size = r.size
        st.frameCam(size.w, size.h)
      }
    }

    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const st = useStore.getState()
      const r = rendererRef.current
      // Every wheel gesture dollies, including the trackpad pinch — in a volume
      // there is no second thing for the wheel to mean, and mapping plain wheel to
      // a pan (as the flat view does, correctly, for a map) would make the most
      // common gesture the least useful one.
      if (e.shiftKey && r) st.panCam(-e.deltaX, -e.deltaY, r.size.w)
      else st.dollyCam(Math.exp(e.deltaY * (e.ctrlKey || e.metaKey ? 0.01 : 0.0022)))
    }

    const onLeave = () => {
      useStore.getState().hover(null)
      setOverNode(false)
    }

    // A finger that lands on a caption is handed here (`labelTouch.ts`, D340).
    const releaseTouch = setTouchTarget(canvas)
    canvas.addEventListener('pointerdown', onDown)
    canvas.addEventListener('pointermove', onMove)
    canvas.addEventListener('pointerup', onUp)
    canvas.addEventListener('pointercancel', onUp)
    // A capture lost without an up ends the press; after a real up it is a no-op.
    canvas.addEventListener('lostpointercapture', onUp)
    canvas.addEventListener('pointerleave', onLeave)
    canvas.addEventListener('dblclick', onDouble as unknown as EventListener)
    canvas.addEventListener('wheel', onWheel, { passive: false })

    return () => {
      releaseTouch()
      canvas.removeEventListener('pointerdown', onDown)
      canvas.removeEventListener('pointermove', onMove)
      canvas.removeEventListener('pointerup', onUp)
      canvas.removeEventListener('pointercancel', onUp)
      canvas.removeEventListener('lostpointercapture', onUp)
      canvas.removeEventListener('pointerleave', onLeave)
      canvas.removeEventListener('dblclick', onDouble as unknown as EventListener)
      canvas.removeEventListener('wheel', onWheel)
    }
  }, [])

  /* ── keyboard ──────────────────────────────────────────────────────── */

  /**
   * Arrow keys orbit; ⇧ raises and lowers.
   *
   * Held here rather than in App.tsx's global handler because these bindings only
   * exist while this canvas is mounted, and a global that had to test the view mode
   * would be a second place that knows about it. Ignored while the user is typing.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable))
        return
      if (e.metaKey || e.ctrlKey) return
      const st = useStore.getState()
      const step = e.shiftKey ? 0.16 : 0.075
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault()
          st.orbitCam(-step, 0)
          break
        case 'ArrowRight':
          e.preventDefault()
          st.orbitCam(step, 0)
          break
        case 'ArrowUp':
          e.preventDefault()
          if (e.shiftKey) st.raiseCam(90)
          else st.orbitCam(0, step)
          break
        case 'ArrowDown':
          e.preventDefault()
          if (e.shiftKey) st.raiseCam(-90)
          else st.orbitCam(0, -step)
          break
        case '+':
        case '=':
          e.preventDefault()
          st.dollyCam(0.86)
          break
        case '-':
          e.preventDefault()
          st.dollyCam(1.16)
          break
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div ref={hostRef} style={{ position: 'absolute', inset: 0 }}>
      <canvas
        ref={canvasRef}
        data-drag={dragging || undefined}
        data-hot={overNode && !dragging ? true : undefined}
        aria-label="Reasoning graph, spatial view"
        role="img"
      />
    </div>
  )
}
